import { ChecksForUnderstandingSubmissionsContextProvider } from "@/assignments/useChecksForUnderstandingSubmissionsContext/useChecksForUnderstandingSubmissionsContext";
import ContentVeil from "@/components/ContentVeil";
import { BannerMode } from "@/types/BannerMode.interface";
import ContentLevel from "@/types/models/ContentLevel.interface";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import ArticleBody from "@/view/ContentDisplay/ArticleBody";
import { rootCss } from "@/view/ContentDisplay/ContentDisplay.style";
import ContentDisplayDetails from "@/view/ContentDisplay/ContentDisplayDetails";
import ContentDisplayHeaderMedia from "@/view/ContentDisplay/ContentDisplayHeaderMedia";
import TeacherTools from "@/view/ContentDisplay/TeacherTools";
import SecondaryActionsToolbar from "@/view/SecondaryActionsToolbar";
import { IMetadata } from "@/view/utils/decodableContent";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";

interface ArticleDisplayProps {
  bannerMode: BannerMode;
  content: any; // TODO: replace 'any' with appropriate type. Cannot use IContent because it lacks some properties used downstream
  contentDisplayRef: any; // TODO: replace 'any' with appropriate type
  contentMetadata: IMetadata[];
  currentLevel?: ContentLevel | undefined;
  isVocabularySupportEnabled: boolean;
  keyTermDataContentId: string;
  legacyArticleId: number;
  matchedAssignments: any[]; // TODO: replace 'any' with appropriate type
  rawText: any;
  readAloudButtonRef: any; // TODO: replace 'any' with appropriate type
  shouldShowMobileActions: boolean;
  toggleVocabularySupport: (value: boolean) => void;
  user: any; // TODO: replace 'any' with appropriate type
}

interface UnauthArticleDisplayProps {
  content: any; // TODO: replace 'any' with appropriate type
  currentLevel: ContentLevel | undefined;
}

const ArticleDisplay = ({
  content,
  currentLevel,
  contentMetadata,
  isVocabularySupportEnabled,
  keyTermDataContentId,
  matchedAssignments,
  rawText,
  readAloudButtonRef,
  shouldShowMobileActions,
  toggleVocabularySupport,
  bannerMode,
  legacyArticleId,
  contentDisplayRef,
  user,
}: ArticleDisplayProps) => {
  const { isTeacher, isStudent } = useGetUserProperties();
  const { updatedAssignment: currentAssignment } =
    useAssignmentReducerContext();

  return (
    <div css={rootCss} ref={contentDisplayRef}>
      <ContentDisplayHeaderMedia
        contentData={content.data}
        currentLevel={currentLevel}
      />
      <ContentDisplayDetails
        content={content}
        contentMetadata={contentMetadata}
        isTeacher={isTeacher}
        isStudent={isStudent}
        currentLevel={currentLevel}
        bannerMode={bannerMode}
      />
      <SecondaryActionsToolbar
        bannerMode={bannerMode}
        content={content}
        currentLevel={currentLevel}
        matchedAssignments={matchedAssignments}
        readAloudButtonRef={readAloudButtonRef}
        shouldShowMobileActions={shouldShowMobileActions}
        user={user}
      />
      <TeacherTools
        content={content}
        currentLevel={currentLevel}
        contentMetadata={contentMetadata}
      />
      <ChecksForUnderstandingSubmissionsContextProvider
        contentId={content.id}
        currentAssignment={currentAssignment}
      >
        <ArticleBody
          content={content}
          contentMetadata={contentMetadata}
          isVocabularySupportEnabled={isVocabularySupportEnabled}
          keyTermDataContentId={keyTermDataContentId}
          language={currentLevel?.language}
          legacyArticleId={legacyArticleId}
          levelId={currentLevel?.id || ""}
          lexile={`${currentLevel?.lexile || ""}`}
          rawText={rawText}
          toggleVocabularySupport={toggleVocabularySupport}
        />
      </ChecksForUnderstandingSubmissionsContextProvider>
    </div>
  );
};

const Unauth = ({ content, currentLevel }: UnauthArticleDisplayProps) => {
  return (
    <div css={rootCss}>
      <ContentDisplayHeaderMedia
        contentData={content.data}
        currentLevel={currentLevel}
      />
      <ContentDisplayDetails.Unauth
        content={content}
        currentLevel={currentLevel}
      />
      <ContentVeil />
    </div>
  );
};

ArticleDisplay.Unauth = Unauth;

export default ArticleDisplay;
